import { Fragment, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { NewUploadDialog } from '../components/NewUploadDialog';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './AdminOrderListPage.module.css';
import { queryKeys } from '../api/queryKeys';
import type { LabCounts } from '../api/lab';

interface AdminOrderRoll {
  roll_id: string;
  roll_name: string;
}

interface AdminOrder {
  order_id: string;
  shopify_order_number: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  created_at: string;
  scan_count: number;
  scan_ready: boolean;
  fulfillment_status: string | null;
  retention_expires_at: string | null;
  expired: boolean;
  /* Optional so a response from a not-yet-deployed backend still renders. */
  rolls?: AdminOrderRoll[];
  /** When the lab booked its rolls in; absent until it has. */
  booked_in_at?: string | null;
  /** Where the order is, from it and its rolls. */
  phase?: OrderPhase;
  /** The customer's negatives the lab is holding for a companion order. */
  held_negatives?: number;
}

type OrderPhase = 'expired' | 'cancelled' | 'awaiting_film' | 'pending' | 'ready' | 'developed' | 'received' | 'in_lab';

/** Each phase's badge. Pending is an older order with nothing to go on: still "Awaiting Upload". */
const PHASE_BADGES: Record<OrderPhase, { label: string; tone: 'ready' | 'booked' | 'awaiting' }> = {
  expired: { label: 'Expired', tone: 'awaiting' },
  cancelled: { label: 'Cancelled', tone: 'awaiting' },
  awaiting_film: { label: 'Awaiting film', tone: 'awaiting' },
  pending: { label: 'Awaiting Upload', tone: 'awaiting' },
  received: { label: 'Film received', tone: 'booked' },
  in_lab: { label: 'In the lab', tone: 'booked' },
  developed: { label: 'Developed', tone: 'booked' },
  ready: { label: 'Ready', tone: 'ready' },
};

interface AdminOrders {
  orders: AdminOrder[];
  /* Optional so a response from a not-yet-deployed backend still renders. */
  lab?: LabCounts;
}

async function fetchAdminOrders(handleUnauthorized: () => void): Promise<AdminOrders> {
  const res = await fetch('/api/v1/admin/orders', { credentials: 'include' });
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  if (!res.ok) {
    throw new Error('Failed to fetch orders');
  }
  const data = await res.json();
  return { orders: data.orders, lab: data.lab };
}

export function AdminOrderListPage() {
  usePageTitle('Admin — Orders');
  const { handleUnauthorized, logout } = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // The customer id is what feature-flag allowlists take, and nothing else in
  // the admin shows it.
  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1500);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — silently no-op.
    }
  };

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const handleSyncOrders = useCallback(async () => {
    setIsSyncing(true);
    try {
      const res = await fetch('/api/v1/admin/orders/sync', {
        method: 'POST',
        credentials: 'include',
      });
      if (res.status === 202) {
        showToast('Sync started', 'success');
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: queryKeys.admin.orders() });
          setIsSyncing(false);
        }, 10000);
      } else if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        setIsSyncing(false);
      } else {
        showToast('Failed to start sync. Please try again.', 'error');
        setIsSyncing(false);
      }
    } catch {
      showToast('Failed to start sync. Please try again.', 'error');
      setIsSyncing(false);
    }
  }, [queryClient, showToast, handleUnauthorized]);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.admin.orders(),
    queryFn: () => fetchAdminOrders(handleUnauthorized),
  });
  const orders = data?.orders;
  const lab = data?.lab;

  const toggleRow = useCallback((orderId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  // Orders with at least one roll — the only ones the expand toggle applies to,
  // so "Expand all" doesn't claim to have expanded rows that have nothing to show.
  const expandableOrderIds = (orders ?? [])
    .filter((o) => (o.rolls?.length ?? 0) > 0)
    .map((o) => o.order_id);
  const allExpanded =
    expandableOrderIds.length > 0 &&
    expandableOrderIds.every((id) => expandedRows.has(id));

  const toggleAllRows = useCallback(() => {
    setExpandedRows(allExpanded ? new Set() : new Set(expandableOrderIds));
  }, [allExpanded, expandableOrderIds]);

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.heading}>Admin — Orders</h1>
        <div className={styles.headerActions}>
          <button
            onClick={() => setDialogOpen(true)}
            className={styles.newUploadBtn}
          >
            New Upload
          </button>
          <button
            onClick={handleSyncOrders}
            disabled={isSyncing}
            className={styles.syncBtn}
          >
            {isSyncing ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                Syncing...
              </>
            ) : (
              'Sync Orders'
            )}
          </button>
          <Link to="/admin/lab" className={styles.printOrdersLink}>
            Lab
            {lab && (lab.develop > 0 || lab.ready > 0 || lab.overdue > 0) && (
              <span className={styles.labCount}>
                {' '}
                (
                {[
                  lab.develop > 0 ? `${lab.develop} to develop` : '',
                  lab.ready > 0 ? `${lab.ready} to mark ready` : '',
                  lab.overdue > 0 ? `${lab.overdue} overdue` : '',
                ]
                  .filter(Boolean)
                  .join(', ')}
                )
              </span>
            )}
          </Link>
          <Link to="/admin/print-orders" className={styles.printOrdersLink}>Print Orders</Link>
          <Link to="/admin/film-stocks" className={styles.printOrdersLink}>Film Stocks</Link>
          <button onClick={logout} className={styles.logoutBtn}>Logout</button>
        </div>
      </div>

      {isLoading && <p className={styles.loading}>Loading orders...</p>}
      {error && <p className={styles.error}>Error loading orders.</p>}

      {orders && orders.length > 0 && (
        <div className={styles.tableWrapper}>
          <div className={styles.tableToolbar}>
            <button
              type="button"
              onClick={toggleAllRows}
              disabled={expandableOrderIds.length === 0}
              aria-expanded={allExpanded}
              className={styles.expandAllBtn}
            >
              {allExpanded ? 'Collapse all roll names' : 'Expand all roll names'}
            </button>
          </div>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>
                  <span className={styles.srOnly}>Rolls</span>
                </th>
                <th>Order #</th>
                <th>Customer</th>
                <th>Customer Email</th>
                <th>Date</th>
                <th>Scans</th>
                <th>Status</th>
                <th>Fulfilled</th>
                <th>Retention Expires</th>
                <th>Expired</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const rolls = order.rolls ?? [];
                const expanded = expandedRows.has(order.order_id);
                return (
                  <Fragment key={order.order_id}>
                    <tr>
                      <td>
                        {rolls.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleRow(order.order_id)}
                            className={styles.expandBtn}
                            aria-expanded={expanded}
                            aria-label={
                              expanded
                                ? `Hide roll names for order ${order.shopify_order_number}`
                                : `Show roll names for order ${order.shopify_order_number}`
                            }
                          >
                            <svg
                              width="10"
                              height="10"
                              viewBox="0 0 10 10"
                              fill="none"
                              aria-hidden="true"
                              style={{
                                transform: expanded ? 'rotate(90deg)' : 'none',
                                transition: 'transform 0.15s',
                              }}
                            >
                              <path d="M2 1L8 5L2 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            <span className={styles.rollCount}>{rolls.length}</span>
                          </button>
                        )}
                      </td>
                      <td>
                        {order.shopify_order_number}
                      </td>
                      <td>
                        {/* A nameless order shows an em dash, matching the email column. */}
                        {order.customer_name || '—'}
                        {!!order.held_negatives && (
                          <span className={styles.heldBadge}>
                            {order.held_negatives} held {order.held_negatives === 1 ? 'negative' : 'negatives'}
                          </span>
                        )}
                        <div className={styles.idCell}>
                          <span className={styles.customerId}>{order.customer_id}</span>
                          <button
                            type="button"
                            className={styles.copyBtn}
                            onClick={() => copyId(order.customer_id)}
                            aria-label={`Copy customer ID ${order.customer_id}`}
                            title="Copy customer ID (for feature-flag allowlists)"
                          >
                            {copiedId === order.customer_id ? 'Copied' : 'Copy'}
                          </button>
                        </div>
                      </td>
                      <td>{order.customer_email || '—'}</td>
                      <td>{new Date(order.created_at).toLocaleDateString()}</td>
                      <td>{order.scan_count}</td>
                      <td>
                        {/* Keyed off scan_ready, not the scan count: an order whose
                            rolls all came back empty is finished with zero scans,
                            and reading "Awaiting Upload" would send someone back to
                            upload images that don't exist. */}
                        {order.phase && PHASE_BADGES[order.phase] ? (
                          <span
                            className={
                              { ready: styles.badgeReady, booked: styles.badgeBooked, awaiting: styles.badgeAwaiting }[
                                PHASE_BADGES[order.phase].tone
                              ]
                            }
                          >
                            {PHASE_BADGES[order.phase].label}
                          </span>
                        ) : order.scan_ready ? (
                          <span className={styles.badgeReady}>Ready</span>
                        ) : order.booked_in_at ? (
                          <span className={styles.badgeBooked}>Booked in</span>
                        ) : (
                          <span className={styles.badgeAwaiting}>Awaiting Upload</span>
                        )}
                      </td>
                      <td>
                        {order.fulfillment_status === 'fulfilled' ? 'Yes' :
                         order.fulfillment_status === 'partial' ? 'Partial' : 'No'}
                      </td>
                      <td>
                        {order.retention_expires_at
                          ? new Date(order.retention_expires_at).toLocaleDateString()
                          : '—'}
                      </td>
                      <td>{order.expired ? 'Yes' : 'No'}</td>
                      <td>
                        {order.scan_ready ? (
                          <Link to={`/admin/orders/${order.order_id}/upload`}>View</Link>
                        ) : (
                          <span className={styles.actionLinks}>
                            <Link
                              to={`/admin/orders/${order.order_id}/book-in`}
                              aria-label={`Book in ${order.shopify_order_number}`}
                            >
                              Book in
                            </Link>
                            <Link to={`/admin/orders/${order.order_id}/upload`}>Upload Scans</Link>
                          </span>
                        )}
                      </td>
                    </tr>
                    {expanded && rolls.length > 0 && (
                      <tr>
                        <td colSpan={11} className={styles.expandedRow}>
                          <ul className={styles.rollList}>
                            {rolls.map((roll) => (
                              <li key={roll.roll_id} className={styles.rollName}>
                                {roll.roll_name}
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {orders && orders.length === 0 && <p className={styles.empty}>No orders found.</p>}

      <NewUploadDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onResolved={(orderId) => {
          setDialogOpen(false);
          navigate(`/admin/orders/${orderId}/upload`);
        }}
      />

      {toast && (
        <div
          className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      )}
    </main>
  );
}
